import React, { useCallback, useEffect, useState } from 'react';
import { trackEvent } from 'utils/analytics';
import { SpotInterface } from 'types';
import { InsightsPeriod, TransientDrivers } from 'types/PropertyInsights';
import { propertyInsightsClient } from 'clients';
import { HTTPError } from 'clients/HTTPClient';
import { UrlUpdaterFunctions } from 'hooks/useFilters';
import barChartImg from 'assets/barChart.png';
import EmptyWidget from 'components/EmptyWidget/EmptyWidget';
import WidgetErrorMessage from 'components/WidgetErrorMessage/WidgetErrorMessage';
import WidgetBox from 'components/widgets/WidgetBox';
import AGSimpleSelect from 'components/AGSelect/AGSimpleSelect/AGSimpleSelect';
import HighlightInfo from '../HighlightInfo/HighlightInfo';
import { formatCount, insightsPeriodOptions, FREQUENT_COLOR, ONE_TIME_COLOR, RETURN_COLOR } from '../helpers';
import TransientDriversSkeleton from './TransientDriversSkeleton';
import styles from './TransientDriversWidget.module.css';

type TransientDriversWidgetProps = {
    spot: SpotInterface;
    filters: { transientDriversPeriod: InsightsPeriod };
    urlUpdater: UrlUpdaterFunctions<{ transientDriversPeriod: InsightsPeriod }>;
};

function TransientDriversWidget({ spot, filters, urlUpdater }: TransientDriversWidgetProps): JSX.Element {
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [report, setReport] = useState<TransientDrivers | null>(null);

    const getTransientDrivers = useCallback(async () => {
        setLoading(true);
        setErrorMessage('');
        try {
            const response = await propertyInsightsClient.getTransientDrivers(
                spot.uuid,
                filters.transientDriversPeriod
            );
            setReport(response);
        } catch (e) {
            if (HTTPError.isHTTPError(e)) {
                setErrorMessage(e.message);
            } else {
                setErrorMessage(e?.toString() || 'Something went wrong');
            }
        } finally {
            setLoading(false);
        }
    }, [spot.uuid, filters.transientDriversPeriod]);

    useEffect(() => {
        getTransientDrivers();
    }, [spot.uuid, getTransientDrivers, filters.transientDriversPeriod]);

    function handleTransientDriversPeriodSelection(selection: string) {
        urlUpdater.transientDriversPeriod(selection);
        trackEvent('Changed Transient Drivers Timeframe', {
            spot: spot.pk,
            spotName: spot.name,
            timeframe: selection,
        });
    }

    const isLoading = loading || !report;
    const isEmpty = report && report.total_drivers === 0;

    return (
        <WidgetBox
            title="Transient Drivers"
            rightElement={
                <AGSimpleSelect
                    options={insightsPeriodOptions}
                    selected={filters.transientDriversPeriod}
                    onSelect={handleTransientDriversPeriodSelection}
                    width="160px"
                />
            }
        >
            {errorMessage ? (
                <WidgetErrorMessage message={errorMessage} onRetry={getTransientDrivers} />
            ) : isLoading ? (
                <TransientDriversSkeleton />
            ) : isEmpty ? (
                <EmptyWidget imgUrl={barChartImg} />
            ) : (
                <div>
                    <div>
                        <p className={styles.sectionTitle}>Highlights</p>
                        <div className={styles.highlightItems}>
                            <div>
                                <p className={styles.highlightLabel}>Repeat Drivers per Day</p>
                                <p className={styles.highlightValue}>{formatCount(report.repeat_drivers_per_day)}</p>
                            </div>

                            <div>
                                <p className={styles.highlightLabel}>Drivers who Parked Twice</p>
                                <p className={styles.highlightValue}>{formatCount(report.second_time_drivers)}</p>
                            </div>

                            <div>
                                <p className={styles.highlightLabel}>Most Frequent Driver</p>
                                <p className={styles.highlightValue}>
                                    {formatCount(report.most_frequent_driver_visits)}
                                    <span>Visits</span>
                                </p>
                            </div>

                            <div>
                                <p className={styles.highlightLabel}>Drivers Who Parked Once</p>
                                <p className={styles.highlightValue}>{formatCount(report.non_returning_drivers)}</p>
                            </div>
                        </div>
                    </div>
                    <div className={styles.divider} />
                    <div>
                        <div className={styles.breakdownInfoHeader}>
                            <p className={styles.sectionTitle}>Breakdown by Type</p>
                            <HighlightInfo text={report.dominant_category_label} />
                        </div>
                        <div className={styles.breakdownInfoContainer}>
                            <div className={styles.breakdownInfoItem}>
                                <div className={styles.infoTitle}>
                                    <div className={styles.infoColorRef} style={{ backgroundColor: ONE_TIME_COLOR }} />
                                    <p className={styles.infoLabel}>One-time</p>
                                </div>
                                <p className={styles.infoDescription}>Parked once and new drivers</p>
                                <p className={styles.infoPct}>{report.one_time_pct}%</p>
                            </div>

                            <div className={styles.breakdownInfoItem}>
                                <div className={styles.infoTitle}>
                                    <div className={styles.infoColorRef} style={{ backgroundColor: RETURN_COLOR }} />
                                    <p className={styles.infoLabel}>Return</p>
                                </div>
                                <p className={styles.infoDescription}>Parked from 2 to 5 times</p>
                                <p className={styles.infoPct}>{report.return_pct}%</p>
                            </div>

                            <div className={styles.breakdownInfoItem}>
                                <div className={styles.infoTitle}>
                                    <div className={styles.infoColorRef} style={{ backgroundColor: FREQUENT_COLOR }} />
                                    <p className={styles.infoLabel}>Frequent</p>
                                </div>
                                <p className={styles.infoDescription}>Parked more than 5 times</p>
                                <p className={styles.infoPct}>{report.frequent_pct}%</p>
                            </div>
                        </div>

                        <div className={styles.bars}>
                            <div
                                className={styles.bar}
                                style={{ height: `${report.one_time_pct}%`, backgroundColor: ONE_TIME_COLOR }}
                            />
                            <div
                                className={styles.bar}
                                style={{ height: `${report.return_pct}%`, backgroundColor: RETURN_COLOR }}
                            />
                            <div
                                className={styles.bar}
                                style={{ height: `${report.frequent_pct}%`, backgroundColor: FREQUENT_COLOR }}
                            />
                        </div>
                    </div>
                </div>
            )}
        </WidgetBox>
    );
}

export default TransientDriversWidget;
